// stripe.js — thin client wrapper around the /api/stripe-* endpoints.
//
// On iOS this whole module is unused; the paywall calls the RC SDK instead
// (Apple's IAP rules require StoreKit for digital subscriptions inside the
// iOS shell). On Electron desktop and the open web, this module is the
// purchase entry point.
//
// Two callable functions:
//
//   startCheckout(plan)   — opens a Stripe Checkout session in the user's
//                           default browser. After the payment lands, our
//                           webhook (api/stripe-webhook.js) updates the
//                           Supabase profile, and the success page polls
//                           subscription.js until the new tier appears.
//
//   openBillingPortal()   — opens the Stripe Customer Portal so the user
//                           can manage their card, change plans, view past
//                           invoices, cancel. We host none of that UI;
//                           Stripe does it all.
//
// Both functions open the resulting Stripe URL through the platform helper:
// Electron forwards it to the OS browser (preload.cjs → shell.openExternal),
// web opens a new tab. We *don't* try to embed Stripe Checkout in our own
// renderer — payment forms must run in the user's real browser for autofill,
// password managers, 3DS challenges, and Stripe's own anti-fraud checks.

import { apiUrl, isDesktop, isWeb, openExternal } from './platform.js';
import { getFreshAccessToken } from './supabase.js';

class StripeError extends Error {
  constructor(message, statusCode) {
    super(message);
    this.name = 'StripeError';
    this.statusCode = statusCode;
  }
}

async function postAuthed(path, body) {
  const token = await getFreshAccessToken();
  if (!token) throw new StripeError('You need to sign in before subscribing.', 401);
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body || {}),
  });
  // Some server errors are returned as text (Stripe webhook 400 path), but
  // the endpoints we own here always return JSON.
  const ct = res.headers.get('content-type') || '';
  const payload = ct.includes('application/json') ? await res.json() : { error: await res.text() };
  if (!res.ok) {
    throw new StripeError(payload.error || `Request failed (${res.status})`, res.status);
  }
  return payload;
}

/**
 * Start a Stripe Checkout session and hand the user off to it.
 *
 * @param {'monthly' | 'annual' | 'lifetime'} plan
 * @returns {Promise<{ ok: true, url: string } | { ok: false, error: Error }>}
 */
export async function startCheckout(plan) {
  try {
    // Pass our current origin as the returnUrl so success/cancel land back
    // on the running app. In Electron this is `file://...` which Stripe
    // would reject, so we fall back to the deployed Vercel URL in that
    // case (the server treats undefined returnUrl as the env default).
    const returnUrl = isWeb() && typeof window !== 'undefined'
      ? window.location.origin
      : undefined;

    const { url } = await postAuthed('/api/stripe-checkout', { plan, returnUrl });

    if (isDesktop()) {
      openExternal(url);
    } else if (typeof window !== 'undefined') {
      // Same-window redirect on the web — Checkout is its own page; coming
      // back to a new tab strands the user.
      window.location.assign(url);
    }
    return { ok: true, url };
  } catch (err) {
    if (err?.statusCode === 401) {
      return { ok: false, userNotSignedIn: true, error: err };
    }
    return { ok: false, error: err };
  }
}

/**
 * Open the Stripe Customer Portal. Same redirect strategy as startCheckout.
 */
export async function openBillingPortal() {
  try {
    const returnUrl = isWeb() && typeof window !== 'undefined'
      ? window.location.origin
      : undefined;
    const { url } = await postAuthed('/api/stripe-portal', { returnUrl });

    if (isDesktop()) {
      openExternal(url);
    } else if (typeof window !== 'undefined') {
      window.location.assign(url);
    }
    return { ok: true, url };
  } catch (err) {
    return { ok: false, error: err };
  }
}

/**
 * Whether Stripe purchases are available in this build. True on desktop and
 * web; false on iOS (RC handles that path). The paywall reads this to decide
 * which set of buttons to wire up.
 */
export function isStripeAvailable() {
  return isDesktop() || isWeb();
}
